/**
 * THE SHARED PERIOD-LABEL RULE (CPO review 2026-09-26): a whole calendar year
 * reads "2024", a calendar quarter "Q1 2025", a calendar month "Mar 2025" —
 * decided from the TYPED window dates, never by editing the label text. Any
 * other window (a fiscal year, a partial span, a point in time) keeps the
 * governed label: there is no typed fiscal calendar to name it by.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

type Day = Readonly<{ year: number; month: number; day: number }>;

function parse(date: string | null | undefined): Day | null {
  const match = ISO_DATE.exec(date ?? '');
  return match ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) } : null;
}

const dayText = (day: Day): string => `${day.day} ${MONTHS[day.month - 1]} ${day.year}`;

function lastDayOf(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export type AskPeriodWindow = Readonly<{
  label: string;
  start?: string | null;
  end?: string | null;
}>;

export function periodLabel(window: AskPeriodWindow): string {
  const start = parse(window.start);
  const end = parse(window.end);
  if (!start || !end) return window.label;
  // A span not aligned to month boundaries has no governed calendar name, and its label can be
  // an ISO range (headed run 2026-09-27 08:50): it reads from its typed days instead.
  if (start.day !== 1 || end.day !== lastDayOf(end.year, end.month)) {
    return `${dayText(start)} to ${dayText(end)}`;
  }
  if (start.year !== end.year) return window.label;
  const months = end.month - start.month + 1;
  if (months === 12) return `${start.year}`;
  if (months === 3 && (start.month - 1) % 3 === 0) return `Q${(start.month + 2) / 3} ${start.year}`;
  if (months === 1) return `${MONTHS[start.month - 1]} ${start.year}`;
  return window.label;
}

/**
 * #8880 — A SNAPSHOT IS ONE DAY, NOT A WINDOW FROM A DATE TO ITSELF. When the typed window
 * starts and ends on the same ISO date, it reads "14 Sep 2026" (callers say "as of …");
 * any other window, or one without typed dates, returns null and keeps its governed label.
 */
export function snapshotDateLabel(
  window: Readonly<{ start?: string | null; end?: string | null }>
): string | null {
  const start = parse(window.start);
  if (!start || window.start !== window.end) return null;
  return `${start.day} ${MONTHS[start.month - 1]} ${start.year}`;
}
